import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { cn } from 'utils/ui/classes';
import _ from 'underscore';
import { Translation, useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { CheckIcon } from '@heroicons/react/20/solid';
import { useFloating, autoUpdate } from '@floating-ui/react';
function comparator(a, b) {
    return a.id === b.id;
}
const Item = (props) => {
    const { item, index } = props;
    const handleItemSelect = (e, selectedItem) => {
        e.stopPropagation();
        props.onSelect(Object.assign(Object.assign({}, selectedItem), { selected: !selectedItem.selected }));
    };
    return (React.createElement("li", { "data-testid": `li-3e034971-${index}`, className: classnames('hover:text-white hover:bg-green-cm relative cursor-default select-none py-2 pl-3 pr-9', {
            'text-green-cm': item.selected,
            'text-gray-700': !item.selected,
        }), onClick: (e) => handleItemSelect(e, item) },
        React.createElement("span", { "data-testid": "span-9df133b6", className: "block font-normal truncate" }, item.localizedName),
        React.createElement("span", { "data-testid": "span-0d4a2d7b", className: "absolute inset-y-0 right-0 flex items-center pr-4" },
            React.createElement(CheckIcon, { className: "h-5 w-5" }))));
};
const DropdownList = ({ children, search, onClose, onSearch, showSearchInput, className, }) => {
    const dropdownRef = useRef();
    useEffect(() => {
        document.addEventListener('click', handleOuterClick);
        return () => {
            document.removeEventListener('click', handleOuterClick);
        };
    }, []);
    function handleOuterClick(e) {
        const { target } = e;
        if (dropdownRef.current && dropdownRef.current.contains(target)) {
            return;
        }
        onClose();
    }
    return (React.createElement("div", { "data-testid": "div-96d31188", ref: dropdownRef, className: "relative", tabIndex: -1 },
        React.createElement("div", { "data-testid": "div-f5b207d4", className: `relative z-50 max-w-350 p-2 mt-1 bg-white shadow-lg rounded-md ${className}` },
            showSearchInput && (React.createElement(Translation, null, (t) => (React.createElement("div", { "data-testid": "div-14c71ed7", className: "pb-2" },
                React.createElement("input", { "data-testid": "input-f1e4f8d9", className: "w-full form-field", tabIndex: -2, placeholder: titleize(t('SEARCH')), value: search, onChange: onSearch }))))),
            React.createElement("ul", { "data-testid": "ul-100d692b", className: cn('max-h-60 overflow-auto', className), role: "listbox", tabIndex: "-1", "aria-labelledby": "listbox-label", "aria-activedescendant": "listbox-option-3" }, children))));
};
const Multiselect = (props) => {
    const { t } = useTranslation();
    const localizeSortBy = (items) => {
        const localizedItems = (items || []).map((item) => {
            const localizedName = item.localizationSlug
                ? t(item.localizationSlug, {
                    count: (item === null || item === void 0 ? void 0 : item.count) || 1,
                })
                : item.name;
            return Object.assign(Object.assign({}, item), { localizedName: item.titleize ? titleize(localizedName) : localizedName });
        });
        return _.sortBy(localizedItems, 'localizedName');
    };
    const incoming = localizeSortBy(props.items);
    const [isOpen, setIsOpen] = useState(false);
    const [items, setItems] = useState(incoming);
    const [filteredItems, setFilteredItems] = useState(incoming);
    const [search, setSearch] = useState('');
    useEffect(() => {
        // Apply localization and sorting to props.items
        const updatedItems = localizeSortBy(props.items);
        // If selectedItems exist and are non-empty, update selected state
        if (props.selectedItems && props.selectedItems.length > 0) {
            const updateSelections = (items) => items.map((item) => (Object.assign(Object.assign({}, item), { selected: props.selectedItems.includes(item.value) })));
            const updatedWithSelections = updateSelections(updatedItems);
            setItems(updatedWithSelections);
            setFilteredItems(updatedWithSelections);
        }
        else {
            // If no selectedItems, update items and filteredItems with sorted data
            setItems(updatedItems);
            setFilteredItems(updatedItems);
        }
    }, [props.items]);
    useEffect(() => {
        // to remove the unwanted effect where clear is not effective.
        if (props.clear) {
            const clearedItems = items.map((item) => {
                item.selected = false;
                return item;
            });
            setItems(clearedItems);
            setFilteredItems(clearedItems);
        }
    }, [props.clear]);
    const { searchKey, valueKey, showSearchInput, showSelectedCount, dataTestId = 'dataTestId-Multi-Select', } = props;
    const label = props.label || 'Items';
    const selectedCount = items.filter((i) => {
        return i.selected === true;
    }).length;
    function handleDropdownButtonClick(e) {
        e.stopPropagation();
        setIsOpen(!isOpen);
    }
    const handleSelect = (selectedItem) => {
        const selectedIndex = items.findIndex((item) => selectedItem[valueKey] === item[valueKey]);
        if (selectedIndex < 0) {
            return;
        }
        const updatedItems = [
            ...items.slice(0, selectedIndex),
            selectedItem,
            ...items.slice(selectedIndex + 1),
        ];
        const filteredValues = filteredItems.map((item) => item[valueKey]);
        setItems(updatedItems);
        setFilteredItems(updatedItems.filter((item) => filteredValues.includes(item[valueKey])));
        props.onSelect(updatedItems.filter((item) => item.selected));
    };
    const handleSearch = (e) => {
        const { target: { value: currentSearch }, } = e;
        setSearch(currentSearch);
        if (currentSearch === '') {
            setFilteredItems(items);
        }
        const searchable = props.items.filter((i) => {
            const re = new RegExp(currentSearch, 'i');
            return re.test(i[searchKey]);
        });
        setFilteredItems(localizeSortBy(searchable));
    };
    function handleClose() {
        setIsOpen(false);
    }
    const { refs, floatingStyles } = useFloating({
        strategy: 'absolute',
        placement: 'bottom-end',
        whileElementsMounted: autoUpdate,
    });
    return (React.createElement("div", { "data-testid": dataTestId, value: props.value },
        React.createElement("button", { "data-testid": "button-db9df9ee", disabled: props.disabled, className: "form-field relative w-full px-4 py-2 pr-10 text-left bg-white border border-gray-300 cursor-default rounded-md text-grey-darker focus:outline-none focus:ring-1 focus:ring-green focus:border-green", onClick: handleDropdownButtonClick, ref: refs.setReference },
            React.createElement("div", { "data-testid": "div-c45533c2", className: "flex space-x-1" },
                React.createElement("span", { "data-testid": "span-2dd5945e", className: "block truncate" }, label),
                showSelectedCount && (React.createElement("span", { "data-testid": "span-856f22b1", className: "truncate" },
                    "(",
                    selectedCount,
                    " ",
                    t('SELECTED'),
                    ")"))),
            React.createElement("span", { "data-testid": "span-52aa5557", className: "absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" },
                React.createElement("svg", { "data-testid": "svg-3e9aea97", className: "w-5 h-5 text-gray-400", xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 20 20", fill: "currentColor", "aria-hidden": "true" },
                    React.createElement("path", { "data-testid": "path-d4d7a795", fillRule: "evenodd", d: "M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z", clipRule: "evenodd" })))),
        isOpen && (React.createElement("div", { "data-testid": "div-c8708277", ref: refs.setFloating, style: Object.assign(Object.assign({}, floatingStyles), { zIndex: 50 }) },
            React.createElement(DropdownList, { search: search, onClose: handleClose, onSearch: handleSearch, showSearchInput: showSearchInput, className: props.dropdownClassName }, filteredItems.map((item, index) => (React.createElement(Item, { item: item, key: index, index: index, onSelect: handleSelect }))))))));
};
Multiselect.propTypes = {
    dropdownClassName: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf([PropTypes.string, PropTypes.object]),
        PropTypes.object,
    ]),
    valueKey: PropTypes.string,
    searchKey: PropTypes.string,
    label: PropTypes.string,
    showSearchInput: PropTypes.bool,
    showSelectedCount: PropTypes.bool,
    comparator: PropTypes.func,
    selectedItems: PropTypes.array,
    testDataId: PropTypes.string,
};
Multiselect.defaultProps = {
    dropdownClassName: {},
    comparator,
    valueKey: 'slug',
    searchKey: 'slug',
    label: 'Items',
    showSearchInput: true,
    showSelectedCount: true,
    selectedItems: [],
    testDataId: 'multiselect',
};
export default Multiselect;
